import { useState, useEffect, useCallback } from "react";
import { Search } from "lucide-react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import TransactionTable from "./TransactionTable";
import { DateRange, TransactionTabType, PaginationInfo } from "../../types/transactionHistory.types";

interface TransactionTabsProps {
  dateRange: DateRange;
  clinicId?: string;
  onOrderClick?: (item: any) => void;
}

const TABS: { key: TransactionTabType; label: string }[] = [
  { key: "other-expenses", label: "Other Expenses" },
  { key: "appointments", label: "Appointments" },
  { key: "medicine-orders", label: "Medicine Orders" },
  { key: "offline-orders", label: "Medicine Orders : In Clinic" },
  { key: "return-orders", label: "Return Orders" },
  { key: "lab-tests", label: "Lab Tests" },
  { key: "offline-labtests", label: "Lab Tests : In Clinic" },
  { key: "deposit-logs", label: "Deposit Logs" },
];

const PAGE_SIZE = 10;

// Maps tab key to the payment-orders `type` param
function getOrderTypeFilter(tabKey: TransactionTabType): string | null {
  const map: Record<string, string> = {
    "medicine-orders": "online-order",
    "offline-orders": "offline-order",
    "return-orders": "online-return",
    "lab-tests": "labtest-order",
    "offline-labtests": "offline-labtest",
    "all": "all",
  };
  return map[tabKey] ?? null;
}

export default function TransactionTabs({ dateRange, clinicId, onOrderClick }: TransactionTabsProps) {
  const [activeTab, setActiveTab] = useState<TransactionTabType>("other-expenses");
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "other-expenses") {
        const res = await TransactionHistoryService.getExpenseListing({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          clinicId,
          page,
          pageSize: PAGE_SIZE,
        });
        let expenses: any[] = [];
        if (Array.isArray(res.data)) {
          expenses = res.data;
        } else if (Array.isArray((res.data as any)?.expenses)) {
          expenses = (res.data as any).expenses;
        }
        setData(expenses);
        setPagination(res.pagination || (res.data as any)?.pagination || null);
      } else if (activeTab === "appointments") {
        const res = await TransactionHistoryService.getAppointmentPaymentDetails({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          clinicIds: clinicId,
          page,
          pageSize: PAGE_SIZE,
        });
        let payments: any[] = [];
        if (Array.isArray(res.data)) {
          payments = res.data;
        } else if (Array.isArray(res.data?.payments)) {
          payments = res.data.payments;
        }
        setData(payments);
        setPagination(res.data?.pagination || (res as any).pagination || null);
      } else if (activeTab === "deposit-logs") {
        const res = await TransactionHistoryService.getBankDepositListing({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          clinicId,
          page,
          pageSize: PAGE_SIZE,
        });
        let deposits: any[] = [];
        if (Array.isArray(res.data)) {
          deposits = res.data;
        } else if (Array.isArray((res.data as any)?.deposits)) {
          deposits = (res.data as any).deposits;
        }
        setData(deposits);
        setPagination(res.pagination || (res.data as any)?.pagination || null);
      } else {
        // Order-based tabs: medicine-orders, return-orders, lab-tests, all
        const typeFilter = getOrderTypeFilter(activeTab);
        const res = await TransactionHistoryService.getPaymentOrders({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          clinicId,
          type: typeFilter || "all",
          page,
          limit: PAGE_SIZE,
          search: searchText || undefined,
        });
        let orders: any[] = [];
        if (Array.isArray(res.data)) {
          orders = res.data;
        } else if (Array.isArray(res.data?.orders)) {
          orders = res.data.orders;
        }
        setData(orders);
        setPagination(res.data?.pagination || null);
      }
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setData([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, dateRange, clinicId, page, searchText]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when tab or date changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, dateRange, clinicId]);

  const handleTabChange = (tab: TransactionTabType) => {
    setActiveTab(tab);
    setSearchText("");
    setPage(1);
  };

  const showSearch = !["other-expenses", "deposit-logs"].includes(activeTab);

  return (
    <div className="space-y-3">
      {/* Tab bar + Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex gap-1 overflow-x-auto pb-1 hiddenScrollbar">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors
                ${activeTab === tab.key
                  ? "bg-secondary text-white"
                  : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showSearch && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-48 focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 transition-all"
            />
          </div>
        )}
      </div>

      {/* Table */}
      <TransactionTable
        type={activeTab}
        data={data}
        pagination={pagination}
        loading={loading}
        currentPage={page}
        onPageChange={setPage}
        onRowClick={onOrderClick}
      />
    </div>
  );
}
